import React, { FC, useEffect, useState } from 'react';
import { Button, Card, Col, Container, Nav, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import useFormList from '../../hooks/api/form/useFormList';
import PaginationComponent from '../../components/pagination/PaginationComponent';
import { useIntl } from 'react-intl';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import EmptyList from '../../components/layout/emptyList/EmptyList';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';
import { useHistory } from 'react-router';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { getText } from '../../utilities/CultureUtilities';
import { FallbackPage } from '../../components/layout/navbar/Navbar';
import { FormState, FormStates } from '../../models/form/form';

const ReportsListPage: FC = () => {
  const [reportSecurity, setReportSecurity] = useState<SecurityModel | null>(null);
  const { forms, setOrganizationId, isLoading, currentPage, getFormsByPage, pageSize, totalCount } = useFormList();
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const history = useHistory();
  const [formState, setFormState] = useState<FormState>(FormStates.Active);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (security) {
      setReportSecurity(getSecuritySection(security, SecuritySections.REPORT));
    }
    if (reportSecurity && !reportSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, reportSecurity]);

  const renderHeader = () => {
    return (
      <>
        <thead>
          <tr>
            <th className="text-start">
              <FormattedMessage id={'formList.columns.formName'}></FormattedMessage>
            </th>
            <th className="text-center">
              <FormattedMessage id={'formList.columns.submission'}></FormattedMessage>
            </th>
            <th className="text-center">
              <FormattedMessage id={'formList.columns.lastUpdate'}></FormattedMessage>
            </th>
            <th className="text-center">
              <FormattedMessage id={'formList.columns.editedBy'}></FormattedMessage>
            </th>
            <th className="text-end">
              <FormattedMessage id={'formList.columns.actions'}></FormattedMessage>
            </th>
          </tr>
        </thead>
      </>
    );
  };
  const renderSubmission = (data) => {
    if (data.submissionLimit) {
      return (
        <span>
          {data.submissionCount}/{data.submissionLimit}
        </span>
      );
    } else {
      return <span>{data.submissionCount}</span>;
    }
  };

  const renderLastUpdate = (lastUpdate) => {
    return (
      <Moment fromNow utc>
        {lastUpdate}
      </Moment>
    );
  };

  const renderEditedBy = (editedBy) => {
    return (
      <span>
        {editedBy?.firstName} {editedBy?.name}
      </span>
    );
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  const renderRows = () => {
    return (
      <>
        <tbody>
          {forms?.map((form, index) => (
            <tr key={index}>
              <td className="text-start">{getText(form.name, intl.locale)}</td>
              <td className="text-center">{renderSubmission(form)}</td>
              <td className="text-center">{renderLastUpdate(form.modificationDate)}</td>
              <td className="text-center">{renderEditedBy(form.editedBy)}</td>
              <td className="d-flex justify-content-end text-end button-column">
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'reports.viewReports' }))}
                  placement="top"
                >
                  <Button
                    variant="primary"
                    className="d-inline-flex align-items-center justify-content-center me-1"
                    onClick={() => history.push(`/reports/${form.id}`)}
                    disabled={!reportSecurity?.read}
                  >
                    <span className="mdi mdi-view-list-outline"></span>
                  </Button>
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  return (
    <div className="reports-list page-content">
      <Container fluid>
        <PageTitle title={intl.formatMessage({ id: 'navbar.settings.reports.title' })}></PageTitle>
        <Row>
          <Col xl={12}>
            <Card>
              <Card.Body>
                <Nav
                  className="justify-content-end mb-3"
                  variant="pills"
                  defaultActiveKey="active"
                  activeKey={formState}
                >
                  <Nav.Item>
                    <Nav.Link
                      eventKey="Active"
                      onClick={() => {
                        setFormState(FormStates.Active);
                        getFormsByPage(1, FormStates.Active);
                      }}
                    >
                      {intl.formatMessage({ id: 'formList.columns.active' })}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="Archived"
                      onClick={() => {
                        setFormState(FormStates.Archived);
                        getFormsByPage(1, FormStates.Archived);
                      }}
                    >
                      {intl.formatMessage({ id: 'formList.columns.archived' })}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                {isLoading && <LoadingSpinner />}
                {!isLoading && forms && forms.length > 0 && (
                  <Table>
                    {renderHeader()}
                    {renderRows()}
                  </Table>
                )}
                {!isLoading && forms && forms.length == 0 && (
                  <div className="d-flex flex-column justify-content-center align-items-center p-4">
                    <EmptyList message={intl.formatMessage({ id: 'reports.noReports' })} />
                    <Link to={`/registrations`}>
                      <Button>{intl.formatMessage({ id: 'reports.toForms' })}</Button>
                    </Link>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {!isLoading && forms && forms.length > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            onChange={(val) => getFormsByPage(val, formState)}
          />
        )}
      </Container>
    </div>
  );
};

export default ReportsListPage;
